import React from 'react';
import { motion } from 'framer-motion';

const DebtShoppingLandingPage = ({ goToInput, theme }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Stop Shopping While in Debt: Break the Cycle
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Manage Your Spending While Paying Down Debt</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track purchases, identify emotional spending triggers, and develop strategies to control shopping while getting debt-free.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Purchase
            </motion.button>
          </div>
          <div className="text-8xl">💸</div>
        </div>
      </div>
      
      {/* The debt and shopping cycle section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">The Debt and Shopping Cycle: Why It's Hard to Stop</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😔</span>
              <div>
                <h3 className="font-bold dark:text-white">Stress-Induced Shopping</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Financial stress from debt often triggers emotional spending as a temporary escape.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🤷‍♀️</span>
              <div>
                <h3 className="font-bold dark:text-white">What's One More Purchase?</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  When already in debt, the psychological barrier to additional spending weakens.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⛓️</span>
              <div>
                <h3 className="font-bold dark:text-white">Cycle of Guilt and Spending</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Post-purchase guilt creates more negative emotions, which can trigger more shopping.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💳</span>
              <div>
                <h3 className="font-bold dark:text-white">Easy Credit Access</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Having credit cards and buy-now-pay-later options makes impulsive spending too convenient.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Our Tool Helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Guilt Gauge Helps You Break the Debt-Shopping Cycle</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Identify Emotional Spending Triggers
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Recognize when stress, anxiety or shame about debt lead to shopping urges.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Calculate True Cost of Purchases
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              See how much each purchase really costs when factoring in interest and debt payoff delay.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Track Debt vs. Spending Balance
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Monitor how your spending impacts your debt payoff timeline with visual progress trackers.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Develop Debt-Conscious Shopping Habits
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Build a personalized framework for making spending decisions that support your debt payoff goals.
            </p>
          </div>
        </div>
      </section>
      
      {/* Financial Impact Visualization */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">The Real Cost of Shopping While in Debt</h2>
        
        <div className="grid grid-cols-2 gap-4 text-center mb-4">
          <div>
            <div className="text-2xl font-bold dark:text-white">$300</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Purchase Price</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">$386</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">True Cost with Interest</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">2 months</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Added to Debt Payoff</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">22%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Effective APR Cost</p>
          </div>
        </div>
        
        <div className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4 italic">
          *Example based on $300 purchase with 18.9% credit card interest and $100 monthly payments
        </div>
      </section>
      
      {/* Spending Personality Types */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Your Debt-Shopping Personality Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🙈</span>
              <h3 className="font-bold mt-2 dark:text-white">Avoidance Spender</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You shop to avoid thinking about your debt, creating a dangerous cycle of increased financial stress.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🎭</span>
              <h3 className="font-bold mt-2 dark:text-white">Status Maintainer</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You continue buying to maintain appearances, afraid others will judge your financial situation.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🔄</span>
              <h3 className="font-bold mt-2 dark:text-white">Cycle Trapped</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Your spending creates guilt, which leads to more spending for temporary relief, creating a vicious cycle.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">⚖️</span>
              <h3 className="font-bold mt-2 dark:text-white">Balance Seeker</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You're actively working to balance necessary purchases with your debt payoff plan.
            </p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Success Stories: Shopping Less, Paying Off More</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I was $24,000 in debt and still shopping to keep up appearances. Tracking my purchases with Guilt Gauge helped me face reality and cut my spending by 60%. I've paid off $10,000 in 8 months."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Michael R.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Former Status Maintainer</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "Every time I felt financial anxiety, I'd buy something to feel better. Guilt Gauge helped me identify this pattern and find healthier stress outlets. I now save $430 monthly toward debt repayment."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Sarah T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Recovering Avoidance Spender</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Practical Strategies Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">8 Practical Strategies for Shopping Less While in Debt</h2>
        <div className="space-y-3">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Calculate Purchase Cost in "Hours Worked"
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8 text-sm">
              Convert prices into hours of work after taxes to see the real value exchange.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Implement the "Debt First" 24-Hour Rule
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8 text-sm">
              Wait 24 hours before non-essential purchases and calculate how that money could reduce debt instead.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Create a Debt-Compatible "Fun Money" Budget
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8 text-sm">
              Set aside a small, guilt-free spending amount that won't impact your debt payoff timeline.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Develop a "Spending Trigger" Action Plan
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8 text-sm">
              Create specific responses to emotional triggers that don't involve spending money.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 h-6 w-6 rounded-full flex items-center justify-center mr-2">5</span>
              Practice "Virtual Shopping"
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8 text-sm">
              Create online wishlists to get the dopamine hit without spending, then revisit later when urges pass.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 h-6 w-6 rounded-full flex items-center justify-center mr-2">6</span>
              Use Cash-Only for Non-Essential Purchases
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8 text-sm">
              The physical act of handing over cash creates stronger awareness than tapping cards or clicking buttons.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 h-6 w-6 rounded-full flex items-center justify-center mr-2">7</span>
              Visualize Your "Debt-Free" Future Date
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8 text-sm">
              Display your projected debt-free date and watch how it changes based on spending decisions.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 h-6 w-6 rounded-full flex items-center justify-center mr-2">8</span>
              Create Accountability with Debt-Payment Celebrations
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8 text-sm">
              Celebrate debt milestones with low-cost rewards to maintain motivation without derailing progress.
            </p>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Shopping While in Debt</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is it wrong to buy anything while in debt?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              No, it's not wrong to make purchases while paying off debt. The key is creating a balanced approach that prioritizes debt repayment while allowing for necessary expenses and occasional treats within a reasonable budget. Guilt Gauge helps you find this balance.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I tell if a purchase is worth delaying debt payoff?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Consider factors like necessity, impact on well-being, true cost with interest, and potential for regret. Track purchases with Guilt Gauge to identify patterns in which purchases you typically regret versus those that provide lasting value despite debt.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I keep shopping even though I know I'm in debt?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Shopping while in debt often stems from emotional triggers like stress relief, denial, habit, status maintenance, or temporary escape from financial anxiety. Guilt Gauge helps identify your personal emotional spending triggers and develop healthier responses.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How much "fun money" is reasonable while paying off debt?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Financial experts typically recommend 5-10% of your after-tax income for discretionary spending while aggressively paying down debt. The exact amount depends on your debt load, interest rates, income, and necessary expenses. Guilt Gauge can help you find your optimal balance.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Balance Shopping & Debt Repayment?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Track your purchases, understand your spending triggers, and create a debt-compatible shopping strategy.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Guilt Score Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Shopping While in Debt</h2>
          <p className="mb-3">
            Shopping while in debt is a common struggle that affects millions of people. The psychological burden of existing debt can paradoxically lead to more spending as a coping mechanism, creating a difficult cycle to break.
          </p>
          <p className="mb-3">
            Our Guilt Gauge Calculator helps you understand and address these patterns by tracking your emotional state during purchases, analyzing your spending habits, and providing personalized strategies that balance reasonable quality of life with effective debt repayment.
          </p>
          <p>
            Whether you're dealing with credit card debt, loans, or buy-now-pay-later obligations, our free tool helps you develop healthier shopping habits while staying committed to your debt freedom journey.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default DebtShoppingLandingPage;
